import React from 'react';
import { Box, Paper, Typography, Stack, LinearProgress } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AssignmentOutlined,
  PlayCircleOutline,
  CheckCircleOutline,
  CancelOutlined,
} from '@mui/icons-material';

export default function ReportStatusBreakdown({ reportData = [], stateId }) {
  // Filter the raw dataset on the fly if a stateId boundary context is active
  const filteredData = stateId
    ? reportData.filter((r) => String(r.stateId) === String(stateId))
    : reportData;

  const total = filteredData.length;

  const metrics = filteredData.reduce(
    (acc, curr) => {
      const status = (curr.plannedReportStatus || 'Not Planned').toLowerCase();
      if (status === 'planned') acc.planned++;
      else if (status === 'ongoing') acc.ongoing++;
      else if (status === 'published' || status === 'approved') acc.published++;
      else acc.notPlanned++;
      return acc;
    },
    { planned: 0, ongoing: 0, published: 0, notPlanned: 0 }
  );

  const statusConfigs = [
    {
      label: 'Published / Approved',
      count: metrics.published,
      color: '#2e7d32',
      icon: <CheckCircleOutline />,
    },
    {
      label: 'Ongoing Entries',
      count: metrics.ongoing,
      color: '#0288d1',
      icon: <PlayCircleOutline />,
    },
    {
      label: 'Schedules Planned',
      count: metrics.planned,
      color: '#ed6c02',
      icon: <AssignmentOutlined />,
    },
    {
      label: 'Unscheduled / Not Planned',
      count: metrics.notPlanned,
      color: '#757575',
      icon: <CancelOutlined />,
    },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: '16px',
        border: '1px solid',
        borderColor: 'divider',
        height: '100%',
      }}
    >
      <Typography variant="subtitle1" fontWeight={700} color="text.primary" gutterBottom>
        Quarterly Schedule Lifecycle
      </Typography>
      <Typography variant="caption" color="textSecondary" display="block" mb={3}>
        Operational overview of facility progress and scheduling milestones
      </Typography>

      <Stack spacing={2.5}>
        {statusConfigs.map((status, index) => {
          const percentage = total > 0 ? Math.round((status.count / total) * 100) : 0;
          return (
            <Box key={index}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ color: status.color, display: 'flex', alignItems: 'center' }}>
                    {status.icon}
                  </Box>
                  <Typography variant="body2" fontWeight={600} color="text.secondary">
                    {status.label}
                  </Typography>
                </Stack>
                <Typography variant="body2" fontWeight={700} color="text.primary">
                  {status.count}{' '}
                  <Box
                    component="span"
                    sx={{ fontWeight: 400, color: 'text.disabled', fontSize: '0.75rem' }}
                  >
                    ({percentage}%)
                  </Box>
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={percentage}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: alpha(status.color, 0.1),
                  '& .MuiLinearProgress-bar': { bgcolor: status.color, borderRadius: 4 },
                }}
              />
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
}
